import React, { useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Container,
    TextField,
    Button,
    Stack,
    Autocomplete,
    Skeleton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    ChevronRight as ChevronRightIcon,
    ContentCopy as ContentCopyIcon,
    Delete as DeleteIcon,
    Instagram as InstagramIcon,
    YouTube as YouTubeIcon,
    Audiotrack as SpotifyIcon,
    MusicNote as TikTokIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService, { User, Company, AdminSocialAccount } from '../services/api';
import { ROUTES_NAMES } from '../components/Layout';
import FavoriteButton from '../components/FavoriteButton';
import { useFavorites } from '../hooks/useFavorites';

function formatCompanyCreatedAt(createdAt?: string): string {
    if (!createdAt) return '—';
    try {
        return new Date(createdAt).toLocaleDateString('es-ES');
    } catch {
        return '—';
    }
}

function formatTokenExpiry(tokenExpiresAt?: string): string {
    if (!tokenExpiresAt) return '—';
    try {
        return new Date(tokenExpiresAt).toLocaleDateString('es-ES');
    } catch {
        return '—';
    }
}

function getTokenStatus(account: AdminSocialAccount): 'valid' | 'expired' | 'missing' {
    if (!account.secretId) return 'missing';
    if (account.tokenExpiresAt && new Date(account.tokenExpiresAt) < new Date()) return 'expired';
    return 'valid';
}

function PlatformIcon({ platform }: { platform: string }) {
    switch (platform) {
        case 'meta': return <InstagramIcon fontSize="small" />;
        case 'tiktok': return <TikTokIcon fontSize="small" />;
        case 'youtube': return <YouTubeIcon fontSize="small" />;
        case 'spotify': return <SpotifyIcon fontSize="small" />;
        default: return null;
    }
}

const PLATFORM_LABEL: Record<string, string> = {
    meta: 'Meta/Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    spotify: 'Spotify',
};

const OAuthConnectionsSection: React.FC = () => {
    const queryClient = useQueryClient();
    const [deleteTarget, setDeleteTarget] = useState<AdminSocialAccount | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const { data, isLoading, error } = useQuery({
        queryKey: ['admin-oauth-connections'],
        queryFn: () => apiService.getAdminSocialAccounts(),
        staleTime: 30_000,
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await apiService.unlinkSocialAccount(id);
        },
        onSuccess: async () => {
            setDeleteTarget(null);
            setDeleteError(null);
            await queryClient.invalidateQueries({ queryKey: ['admin-oauth-connections'] });
        },
        onError: (e: any) => {
            setDeleteError(e?.response?.data?.message || e?.message || 'Error al eliminar');
        },
    });

    const accounts = data?.accounts ?? [];
    const uniqueCompanyIds = useMemo(
        () => new Set(accounts.map((a) => a.companyId)),
        [accounts],
    );

    const statusChip = (account: AdminSocialAccount) => {
        const status = getTokenStatus(account);
        if (status === 'missing') {
            return <Chip label="sin tokens" size="small" color="error" variant="outlined" />;
        }
        if (status === 'expired') {
            return <Chip label="expirado" size="small" color="warning" variant="outlined" />;
        }
        return <Chip label="válido" size="small" color="success" variant="outlined" />;
    };

    return (
        <Paper sx={{ mt: 3 }}>
            <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                    Conexiones OAuth
                </Typography>
                {!isLoading && !error && (
                    <Typography variant="body2" color="text.secondary">
                        {accounts.length} conexión{accounts.length !== 1 ? 'es' : ''} en {uniqueCompanyIds.size} empresa{uniqueCompanyIds.size !== 1 ? 's' : ''}
                    </Typography>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
                    Error al cargar conexiones OAuth
                </Alert>
            )}

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Empresa</TableCell>
                            <TableCell>Podcast</TableCell>
                            <TableCell>Plataforma</TableCell>
                            <TableCell>Cuenta</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Expira token</TableCell>
                            <TableCell align="right">Acción</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={`oauth-skel-${i}`}>
                                    {Array.from({ length: 7 }).map((__, j) => (
                                        <TableCell key={j}><Skeleton /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : accounts.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        No hay conexiones OAuth
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            accounts.map((account) => (
                                <TableRow key={account._id} hover>
                                    <TableCell>
                                        <Stack spacing={0.25}>
                                            <Typography variant="body2" fontWeight="medium">
                                                {account.companyName || '—'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                {account.companyId}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        {account.podcastTitle || account.podcastId ? (
                                            <Stack spacing={0.25}>
                                                <Typography variant="body2">
                                                    {account.podcastTitle || '—'}
                                                </Typography>
                                                {account.podcastId && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                        {account.podcastId}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">—</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <PlatformIcon platform={account.platform} />
                                            <Typography variant="body2">
                                                {PLATFORM_LABEL[account.platform] ?? account.platform}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack spacing={0.25}>
                                            {account.username && (
                                                <Typography variant="body2">@{account.username}</Typography>
                                            )}
                                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                {account.accountId}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{statusChip(account)}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatTokenExpiry(account.tokenExpiresAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Eliminar conexión (DB + SSM)">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => {
                                                    setDeleteError(null);
                                                    setDeleteTarget(account);
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Delete confirmation dialog */}
            <Dialog open={!!deleteTarget} onClose={() => { if (!deleteMutation.isPending) setDeleteTarget(null); }} maxWidth="sm" fullWidth>
                <DialogTitle>Eliminar conexión OAuth</DialogTitle>
                <DialogContent>
                    {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
                    {deleteTarget && (
                        <Box>
                            <Typography gutterBottom>
                                ¿Eliminar la conexión de <strong>{PLATFORM_LABEL[deleteTarget.platform] ?? deleteTarget.platform}</strong> para{' '}
                                <strong>{deleteTarget.companyName}</strong>
                                {deleteTarget.podcastTitle ? ` / ${deleteTarget.podcastTitle}` : ''}?
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Se eliminará el documento de <code>social_accounts</code> y el secreto en AWS SSM (<code>{deleteTarget.secretId ?? 'sin secretId'}</code>).
                                Esta acción no se puede deshacer.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={deleteMutation.isPending}
                        onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
                    >
                        {deleteMutation.isPending ? 'Eliminando…' : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

const EXPLORER_TABS = [
    { id: 'explorer-programs', label: 'Programas' },
    { id: 'explorer-sponsors', label: 'Auspiciadores' },
    { id: 'explorer-livestreams', label: 'Livestreams' },
    { id: 'explorer-compare', label: 'Comparar' },
] as const;

function explorerTabsFromProducts(products: string[] = []): string[] {
    if (products.includes('explorer')) return EXPLORER_TABS.map((t) => t.label);
    return EXPLORER_TABS.filter((t) => products.includes(t.id)).map((t) => t.label);
}

/** Explorer bounds are stored as `YYYY-MM-DD`; parsing them with `new Date()` would shift a day in negative UTC offsets. */
function formatIsoDay(day: string): string {
    const [y, m, d] = day.split('-').map(Number);
    if (!y || !m || !d) return day;
    return new Date(y, m - 1, d).toLocaleDateString('es-ES');
}

function formatDateRange(min?: string | null, max?: string | null): string {
    if (!min && !max) return 'Sin restricción';
    if (!max) return `Desde ${formatIsoDay(min!)}`;
    if (!min) return `Hasta ${formatIsoDay(max)}`;
    return `${formatIsoDay(min)} – ${formatIsoDay(max)}`;
}

/** Row click handlers: plain click navigates in-app, Cmd/Ctrl/middle click opens a new tab. */
function useRowLink() {
    const navigate = useNavigate();
    return (path: string) => ({
        onClick: (e: React.MouseEvent) => {
            if (e.metaKey || e.ctrlKey) {
                window.open(path, '_blank', 'noopener');
                return;
            }
            navigate(path);
        },
        onAuxClick: (e: React.MouseEvent) => {
            if (e.button === 1) window.open(path, '_blank', 'noopener');
        },
    });
}

function compareFavoritesFirst(
    favoriteIds: ReadonlySet<string>,
    a: { id: string; name: string },
    b: { id: string; name: string },
): number {
    const fa = favoriteIds.has(a.id) ? 0 : 1;
    const fb = favoriteIds.has(b.id) ? 0 : 1;
    return fa - fb || a.name.localeCompare(b.name);
}

function useFavoriteCompanyIdSet(): Set<string> {
    const { getFavoriteIdsByType } = useFavorites();
    const ids = getFavoriteIdsByType('company');
    return useMemo(() => new Set(ids), [ids]);
}

const PlanCompanyCell: React.FC<{ companyId: string; name: string; label?: string }> = ({ companyId, name, label }) => (
    <Stack direction="row" spacing={0.5} alignItems="flex-start">
        <FavoriteButton entityType="company" entityId={companyId} />
        <Stack spacing={0.25} sx={{ pt: 0.5 }}>
            <Typography
                component={RouterLink}
                to={`/admin/companies/${companyId}`}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                variant="body2"
                fontWeight="medium"
                sx={{ color: 'inherit' }}
            >
                {name}
            </Typography>
            {label && (
                <Typography variant="caption" color="text.secondary">
                    {label}
                </Typography>
            )}
        </Stack>
    </Stack>
);

const PlansSubsectionHeader: React.FC<{ title: string; summary?: string }> = ({ title, summary }) => (
    <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ flex: 1 }}>
            {title}
        </Typography>
        {summary && (
            <Typography variant="body2" color="text.secondary">
                {summary}
            </Typography>
        )}
    </Box>
);

const ExplorerDatesCell: React.FC<{ company: Company; users?: User[]; usersLoading: boolean }> = ({
    company,
    users,
    usersLoading,
}) => {
    const overrides = (users ?? []).filter((u) => u.dateFilterMinStartDate || u.dateFilterMaxEndDate);
    return (
        <Stack spacing={0.5}>
            <Typography variant="body2">
                <Typography component="span" variant="caption" color="text.secondary">Empresa: </Typography>
                {formatDateRange(company.dateFilterMinStartDate, company.dateFilterMaxEndDate)}
            </Typography>
            {usersLoading ? (
                <Skeleton width={120} />
            ) : (
                overrides.map((u) => (
                    <Typography key={u._id} variant="body2">
                        <Typography component="span" variant="caption" color="text.secondary">{u.name}: </Typography>
                        {formatDateRange(u.dateFilterMinStartDate, u.dateFilterMaxEndDate)}
                    </Typography>
                ))
            )}
        </Stack>
    );
};

const ActiveExplorerPlansTable: React.FC = () => {
    const { data, isLoading, error } = useQuery({
        queryKey: ['admin-explorer-plans'],
        queryFn: () => apiService.getCompanies(1, 1000),
        staleTime: 30_000,
    });
    const favoriteIds = useFavoriteCompanyIdSet();
    const rowLink = useRowLink();

    const explorerCompanies = useMemo(
        () => (data?.companies ?? [])
            .map((company) => ({ company, tabs: explorerTabsFromProducts(company.products) }))
            .filter((row) => row.tabs.length > 0)
            .sort((a, b) => compareFavoritesFirst(
                favoriteIds,
                { id: a.company._id, name: a.company.name },
                { id: b.company._id, name: b.company.name },
            )),
        [data, favoriteIds],
    );

    const userQueries = useQueries({
        queries: explorerCompanies.map(({ company }) => ({
            queryKey: ['admin-company-users', company._id],
            queryFn: () => apiService.getCompanyUsers(company._id),
            staleTime: 30_000,
        })),
    });

    return (
        <>
            <PlansSubsectionHeader
                title="Explorador"
                summary={!isLoading && !error
                    ? `${explorerCompanies.length} empresa${explorerCompanies.length !== 1 ? 's' : ''}`
                    : undefined}
            />

            {error && (
                <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
                    Error al cargar planes del Explorador
                </Alert>
            )}

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Empresa</TableCell>
                            <TableCell>Tabs</TableCell>
                            <TableCell>Fechas de acceso al Explorador</TableCell>
                            <TableCell>Permisos por usuario</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            Array.from({ length: 3 }).map((_, i) => (
                                <TableRow key={`explorer-plan-skel-${i}`}>
                                    {Array.from({ length: 4 }).map((__, j) => (
                                        <TableCell key={j}><Skeleton /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : explorerCompanies.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        Ninguna empresa tiene acceso al Explorador
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            explorerCompanies.map(({ company, tabs }, rowIdx) => (
                                <TableRow
                                    key={company._id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    {...rowLink(`/admin/companies/${company._id}`)}
                                >
                                    <TableCell>
                                        <PlanCompanyCell companyId={company._id} name={company.name} />
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                            {tabs.map((label) => (
                                                <Chip key={label} label={label} size="small" variant="outlined" />
                                            ))}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <ExplorerDatesCell
                                            company={company}
                                            users={userQueries[rowIdx]?.data?.users}
                                            usersLoading={userQueries[rowIdx]?.isLoading ?? false}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {company.enablePerUserProductPermissions ? 'Sí' : 'No'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const ActiveContentPlansSection: React.FC = () => {
    const { data, isLoading, error } = useQuery({
        queryKey: ['admin-content-plans'],
        queryFn: () => apiService.getCompanyContentPlans(),
        staleTime: 30_000,
    });
    const favoriteIds = useFavoriteCompanyIdSet();
    const rowLink = useRowLink();

    const activePlans = useMemo(
        () => (data ?? [])
            .filter((p) => p.enabled)
            .sort((a, b) => compareFavoritesFirst(
                favoriteIds,
                { id: a.companyId, name: a.companyName ?? '' },
                { id: b.companyId, name: b.companyName ?? '' },
            )),
        [data, favoriteIds],
    );
    const podcastCount = activePlans.reduce((n, p) => n + p.scopes.length, 0);

    return (
        <Paper sx={{ mt: 3, pb: 1 }}>
            <Box sx={{ px: 2, pt: 2 }}>
                <Typography variant="h6">
                    Planes activos
                </Typography>
            </Box>

            <PlansSubsectionHeader
                title="Mi contenido / Mis Podcasts"
                summary={!isLoading && !error
                    ? `${activePlans.length} plan${activePlans.length !== 1 ? 'es' : ''} · ${podcastCount} podcast${podcastCount !== 1 ? 's' : ''}`
                    : undefined}
            />

            {error && (
                <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
                    Error al cargar planes
                </Alert>
            )}

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Empresa</TableCell>
                            <TableCell>Podcast</TableCell>
                            <TableCell>Programas</TableCell>
                            <TableCell>Marcas</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={`plan-skel-${i}`}>
                                    {Array.from({ length: 4 }).map((__, j) => (
                                        <TableCell key={j}><Skeleton /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : activePlans.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        No hay planes activos
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            activePlans.flatMap((plan) =>
                                plan.scopes.map((scope, idx) => (
                                    <TableRow
                                        key={`${plan._id}-${scope.podcastId}-${idx}`}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        {...rowLink(`/admin/companies/${plan.companyId}`)}
                                    >
                                        {idx === 0 && (
                                            <TableCell rowSpan={plan.scopes.length} sx={{ verticalAlign: 'top' }}>
                                                <PlanCompanyCell
                                                    companyId={plan.companyId}
                                                    name={plan.companyName || plan.companyId}
                                                    label={plan.label}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Typography variant="body2">
                                                {scope.podcastTitle || scope.podcastId}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {scope.programNames && scope.programNames.length > 0 ? (
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                    {scope.programNames.map((name) => (
                                                        <Chip key={name} label={name} size="small" variant="outlined" />
                                                    ))}
                                                </Stack>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">Todos</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {scope.brandIds && scope.brandIds.length > 0
                                                    ? `${scope.brandIds.length} seleccionada${scope.brandIds.length !== 1 ? 's' : ''}`
                                                    : 'Todas'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )),
                            )
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <ActiveExplorerPlansTable />
        </Paper>
    );
};

const Admin: React.FC = () => {
    const rowLink = useRowLink();
    const queryClient = useQueryClient();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const { getFavoriteIdsByType } = useFavorites();
    const favoriteCompanyIds = getFavoriteIdsByType('company');
    const favoriteIdSet = useMemo(
        () => new Set(favoriteCompanyIds),
        [favoriteCompanyIds],
    );

    // Create company state
    const [newCompanyName, setNewCompanyName] = useState('');
    const [newCompanyOwner, setNewCompanyOwner] = useState<User | null>(null);
    const [createError, setCreateError] = useState<string | null>(null);

    // Create user state
    const [newUserName, setNewUserName] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [createUserError, setCreateUserError] = useState<string | null>(null);

    // Fetch companies
    const { data: companiesData, isLoading, error } = useQuery({
        queryKey: ['admin-companies', page, rowsPerPage],
        queryFn: async () => {
            return await apiService.getCompanies(page + 1, rowsPerPage);
        },
        staleTime: 0,
    });

    const companies = companiesData?.companies || [];
    const totalCompanies = companiesData?.pagination?.total || 0;

    const displayCompanies = useMemo(
        () => companies.filter((c) => !favoriteIdSet.has(c._id)),
        [companies, favoriteIdSet],
    );

    const { data: favoriteCompaniesRows, isFetching: favoriteCompaniesLoading } = useQuery({
        queryKey: ['admin-favorite-companies', favoriteCompanyIds.join(',')],
        queryFn: async (): Promise<Array<{ id: string; company: Company }>> => {
            const rows: Array<{ id: string; company: Company }> = [];
            for (const id of favoriteCompanyIds) {
                try {
                    const company = await apiService.getCompanyById(id);
                    rows.push({ id, company });
                } catch {
                    // Company removed or inaccessible — skip
                }
            }
            return rows;
        },
        enabled: favoriteCompanyIds.length > 0,
    });

    // Fetch admin users for owner selection
    const { data: adminUsersResp } = useQuery({
        queryKey: ['admin-users-for-company-create'],
        queryFn: () => apiService.getAdminUsers(1, 200),
        staleTime: 60_000,
    });
    const adminUsers: User[] = adminUsersResp?.users || [];

    // Create company mutation
    const createCompanyMutation = useMutation({
        mutationFn: async () => {
            setCreateError(null);
            if (!newCompanyName.trim()) throw new Error('Ingresa un nombre');
            const ownerId = newCompanyOwner?._id;
            await apiService.createCompany(newCompanyName.trim(), ownerId);
        },
        onSuccess: async () => {
            setNewCompanyName('');
            setNewCompanyOwner(null);
            await queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
        },
        onError: (e: any) => setCreateError(e?.response?.data?.message || e?.message || 'No se pudo crear la empresa'),
    });

    // Create user mutation
    const createUserMutation = useMutation({
        mutationFn: async () => {
            setCreateUserError(null);
            if (!newUserName.trim()) throw new Error('Ingresa un nombre');
            if (!newUserEmail.trim()) throw new Error('Ingresa un email');
            if (!newUserPassword.trim()) throw new Error('Ingresa una contraseña');
            await apiService.createUser(newUserName.trim(), newUserEmail.trim(), newUserPassword.trim());
        },
        onSuccess: async () => {
            setNewUserName('');
            setNewUserEmail('');
            setNewUserPassword('');
            await queryClient.invalidateQueries({ queryKey: ['admin-users-for-company-create'] });
            setCreateUserError(null);
        },
        onError: (e: any) => setCreateUserError(e?.response?.data?.message || e?.message || 'No se pudo crear el usuario'),
    });

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const tableColSpan = 6;

    if (isLoading) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg">
                <Alert severity="error" sx={{ mt: 3 }}>
                    Error al cargar empresas
                </Alert>
            </Container>
        );
    }

    const showFavoritesBlock = favoriteCompanyIds.length > 0;
    const hasResolvedFavorites = (favoriteCompaniesRows?.length ?? 0) > 0;

    const renderCompanyTableHead = () => (
        <TableHead>
            <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Programas</TableCell>
                <TableCell>Creada</TableCell>
                <TableCell align="center" sx={{ width: 56 }} />
                <TableCell align="right">Acciones</TableCell>
            </TableRow>
        </TableHead>
    );

    return (
        <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
                <Typography variant="h1" gutterBottom>
                    Administración
                </Typography>
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button component={RouterLink} to={ROUTES_NAMES.ADMIN_PRIXI_COMPANIES} variant="outlined" size="small">
                        Empresas Prixi
                    </Button>
                    <Button component={RouterLink} to={ROUTES_NAMES.ADMIN_EMAILING} variant="outlined" size="small">
                        Emailing
                    </Button>
                    <Button component={RouterLink} to={ROUTES_NAMES.ADMIN_METRICS_REPORTS} variant="outlined" size="small">
                        Reportes métricas
                    </Button>
                </Box>

                {/* Create User Section */}
                <Paper sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Crear Nuevo Usuario
                    </Typography>
                    {createUserError && <Alert severity="error" sx={{ mb: 2 }}>{createUserError}</Alert>}
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            label="Nombre"
                            size="small"
                            value={newUserName}
                            onChange={(e) => setNewUserName(e.target.value)}
                            sx={{ flex: 1 }}
                            placeholder="Ej: Juan Pérez"
                        />
                        <TextField
                            label="Email"
                            type="email"
                            size="small"
                            value={newUserEmail}
                            onChange={(e) => setNewUserEmail(e.target.value)}
                            sx={{ flex: 1 }}
                            placeholder="usuario@ejemplo.com"
                        />
                        <TextField
                            label="Contraseña"
                            type="password"
                            size="small"
                            value={newUserPassword}
                            onChange={(e) => setNewUserPassword(e.target.value)}
                            sx={{ flex: 1 }}
                            placeholder="Mínimo 6 caracteres"
                        />
                        <Button
                            variant="contained"
                            onClick={() => createUserMutation.mutate()}
                            disabled={createUserMutation.isPending || !newUserName.trim() || !newUserEmail.trim() || !newUserPassword.trim()}
                        >
                            Crear
                        </Button>
                    </Stack>
                </Paper>

                {/* Create Company Section */}
                <Paper sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Crear Nueva Empresa
                    </Typography>
                    {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            label="Nombre de la empresa"
                            size="small"
                            value={newCompanyName}
                            onChange={(e) => setNewCompanyName(e.target.value)}
                            sx={{ flex: 1 }}
                            placeholder="Ej: Mi Empresa S.A."
                        />
                        <Autocomplete
                            disablePortal
                            options={adminUsers}
                            getOptionLabel={(option: User) => `${option.name} <${option.email}>`}
                            value={newCompanyOwner}
                            onChange={(_e, v) => setNewCompanyOwner(v)}
                            renderInput={(params) => <TextField {...params} label="Owner (opcional)" size="small" />}
                            sx={{ flex: 1, minWidth: 280 }}
                        />
                        <Button
                            variant="contained"
                            onClick={() => createCompanyMutation.mutate()}
                            disabled={createCompanyMutation.isPending || !newCompanyName.trim()}
                        >
                            Crear
                        </Button>
                    </Stack>
                </Paper>

                {showFavoritesBlock && (
                    <Paper sx={{ mt: 3 }}>
                        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                            <Typography variant="h6">
                                Empresas favoritas
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                {renderCompanyTableHead()}
                                <TableBody>
                                    {favoriteCompaniesLoading ? (
                                        Array.from({ length: favoriteCompanyIds.length }).map((_, i) => (
                                            <TableRow key={`fav-skel-${i}`}>
                                                <TableCell><Skeleton width={160} /></TableCell>
                                                <TableCell><Skeleton width={100} /></TableCell>
                                                <TableCell><Skeleton width={80} /></TableCell>
                                                <TableCell><Skeleton width={72} /></TableCell>
                                                <TableCell align="center"><Skeleton variant="circular" width={28} height={28} /></TableCell>
                                                <TableCell align="right"><Skeleton variant="circular" width={28} height={28} /></TableCell>
                                            </TableRow>
                                        ))
                                    ) : !hasResolvedFavorites ? (
                                        <TableRow>
                                            <TableCell colSpan={tableColSpan} align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    No se pudieron cargar las empresas favoritas
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        favoriteCompaniesRows!.map(({ id, company }) => (
                                            <TableRow
                                                key={id}
                                                hover
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover',
                                                    },
                                                }}
                                                {...rowLink(`/admin/companies/${id}`)}
                                            >
                                                <TableCell>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <Typography variant="body1" fontWeight="medium">
                                                            {company.name}
                                                        </Typography>
                                                        <Tooltip title="Copiar ID de la empresa">
                                                            <IconButton
                                                                size="small"
                                                                aria-label="Copiar ID de la empresa"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    void navigator.clipboard.writeText(id);
                                                                }}
                                                            >
                                                                <ContentCopyIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {company.slug}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Por programa
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatCompanyCreatedAt(company.createdAt)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <FavoriteButton entityType="company" entityId={id} />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Configurar empresa">
                                                        <IconButton
                                                            size="small"
                                                        >
                                                            <ChevronRightIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Companies List */}
                <Paper sx={{ mt: 3 }}>
                    <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                        <Typography variant="h6">
                            Empresas
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table>
                            {renderCompanyTableHead()}
                            <TableBody>
                                {companies.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={tableColSpan} align="center">
                                            No hay empresas
                                        </TableCell>
                                    </TableRow>
                                ) : displayCompanies.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={tableColSpan} align="center">
                                            Las empresas de esta página están en &quot;Empresas favoritas&quot; arriba o cambia de página.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    displayCompanies.map((company) => (
                                        <TableRow
                                            key={company._id}
                                            hover
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                },
                                            }}
                                            {...rowLink(`/admin/companies/${company._id}`)}
                                        >
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {company.name}
                                                    </Typography>
                                                    <Tooltip title="Copiar ID de la empresa">
                                                        <IconButton
                                                            size="small"
                                                            aria-label="Copiar ID de la empresa"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                void navigator.clipboard.writeText(company._id);
                                                            }}
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {company.slug}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    Por programa
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatCompanyCreatedAt(company.createdAt)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <FavoriteButton entityType="company" entityId={company._id} />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Configurar empresa">
                                                    <IconButton
                                                        size="small"
                                                    >
                                                        <ChevronRightIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalCompanies}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="Filas por página"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                </Paper>

                <ActiveContentPlansSection />

                <OAuthConnectionsSection />
            </Box>
        </Container>
    );
};

export default Admin;
